// Helpers for resolving Xano-hosted media URLs.
// Xano returns relative paths like "/vault/HyzL86dY/.../file.jpg" and the
// frontend needs to prepend the Xano host for browsers to actually load them.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { EP } from "@/lib/endpoints";

const XANO = (process.env.REACT_APP_XANO_API_URL || "").replace(/\/+$/, "");

export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  if (!XANO) return url;
  return `${XANO}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * Extract a user's avatar URL from any of the field-name variants the Xano
 * endpoints return (`profile_photo`, `profile_photo_url`, `avatar_url`,
 * `avatar`) and resolve it to an absolute URL.
 */
export function resolveAvatarUrl(user) {
  if (!user || typeof user !== "object") return "";
  const raw =
    user.profile_photo_url ||
    user.profile_photo ||
    user.avatar_url ||
    user.avatar ||
    user.photo_url ||
    "";
  return resolveMediaUrl(raw);
}

// -------------------------------------------------------------------
// Media-id resolver — some Xano endpoints (notably /me/saved) return
// only `profile_photo_media_id` for the author. We resolve that id to a
// file URL via `GET /media/{id}` and cache the result in-module so we
// don't refetch across renders.
// -------------------------------------------------------------------

const mediaUrlCache = new Map(); // id -> resolved url (absolute)
const mediaInflight = new Map(); // id -> Promise<string>

async function fetchMediaUrl(id) {
  if (!id) return "";
  if (mediaUrlCache.has(id)) return mediaUrlCache.get(id);
  if (mediaInflight.has(id)) return mediaInflight.get(id);
  const p = (async () => {
    try {
      const res = await api.get(EP.media(id));
      const payload = res?.data?.data || res?.data || {};
      const raw = payload.file_url || payload.url || payload.src || "";
      const url = resolveMediaUrl(raw);
      mediaUrlCache.set(id, url);
      return url;
    } catch {
      mediaUrlCache.set(id, "");
      return "";
    } finally {
      mediaInflight.delete(id);
    }
  })();
  mediaInflight.set(id, p);
  return p;
}

/**
 * React hook that returns a resolved avatar URL for any user shape.
 * Order of resolution:
 *   1. Any inline URL-style field (`profile_photo_url`, `profile_photo`, ...)
 *   2. `profile_photo_media_id` → `GET /media/{id}` (cached)
 * Returns an empty string until resolution completes; the calling
 * component (UserAvatar) then falls back to its initials gradient.
 */
export function useAvatarUrl(user) {
  const inline = resolveAvatarUrl(user);
  const mediaId = user && typeof user === "object" ? user.profile_photo_media_id : null;
  const [url, setUrl] = useState(() => inline || (mediaId ? mediaUrlCache.get(mediaId) || "" : ""));

  useEffect(() => {
    if (inline) {
      setUrl(inline);
      return;
    }
    if (mediaId) {
      const cached = mediaUrlCache.get(mediaId);
      if (cached !== undefined) {
        setUrl(cached);
        return;
      }
      let cancelled = false;
      fetchMediaUrl(mediaId).then((u) => {
        if (!cancelled) setUrl(u);
      });
      return () => { cancelled = true; };
    }
    setUrl("");
  }, [inline, mediaId]);

  return url;
}
